import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { getText } from 'services/languageService';
import { logInAction, resetLogInErrorAction } from 'store/actions/mainPage';
import { createTheme } from '@mui/material/styles';
import {
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Tooltip,
  ListItem,
  List,
} from '@mui/material';

const theme = createTheme();
export interface LogInProps {
  handleClose: () => void;
  open: boolean;
}

export const LogInModal: React.FC<LogInProps> = ({
  handleClose,
  open,
}): JSX.Element => {
  const [loginInput, setLoginInput] = useState<string>('');
  const [passwordInput, setPasswordInput] = useState<string>('');

  const dispatch = useAppDispatch();

  const isLoad = useAppSelector((state) => state.mainPage.isLoading);
  const isLogged = useAppSelector((state) => state.mainPage.isLogged);
  const isError = useAppSelector((state) => state.mainPage.isError);
  const [errorText, setErrorText] = useState('');

  const onChangeLoginInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    const filteredInput = input.replace(/[^a-zA-Z\d@_]/g, '');
    const digitCount = (filteredInput.match(/\d/g) || []).length;
    const limitedInput =
      digitCount > 2 ? filteredInput.slice(0, -1) : filteredInput;

    setLoginInput(limitedInput);
    if (errorText) {
      setErrorText('');
    }
    if (isError) {
      dispatch(resetLogInErrorAction());
    }
  };

  const onChangePasswordInput = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = event.target.value;

    setPasswordInput(input);

    if (errorText) {
      setErrorText('');
    }
    if (isError) {
      dispatch(resetLogInErrorAction());
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (passwordInput.length < 8) {
      setErrorText(getText('SHORT_PASS'));
    }
    if (!loginInput.trim() || !passwordInput.trim()) {
      setErrorText(getText('NEED_LOGIN_PASS'));
    }

    if (loginInput.trim() && passwordInput.length >= 8) {
      const data = {
        login: loginInput,
        password: passwordInput,
      };
      dispatch(logInAction(data));
    }
  };

  useEffect(() => {
    if (isError) {
      setErrorText(getText('WRONG_DATA'));
    }
  }, [isError]);

  const loginTooltips = [
    getText('LOGIN_TL'),
    getText('LOGIN_TL_LANG'),
    getText('LOGIN_TL_DIGITS'),
    getText('LOGIN_TL_SYMBOLSl'),
  ];
  const passwordTooltips = [
    getText('PASSWORD_TL'),
    getText('PASSWORD_TL_LENGTH'),
    getText('PASSWORD_TL_LETTERS'),
    getText('PASSWORD_TL_REG'),
  ];

  const getLoginTooltip = (tooltipArr: string[]) => {
    return (
      <Box sx={{ fontSize: 12 }}>
        <List sx={{ ml: 0 }} dense>
          {tooltipArr.map((item) => {
            return (
              <ListItem key={item} sx={{ pt: 0.5 }}>
                {item}
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  };

  return (
    <Dialog
      fullWidth={true}
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      sx={{ zIndex: 1500 }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', p: 2, pb: 0 }}
      >
        <Typography
          sx={{
            color: (theme) => theme.palette.primary.main,
            fontSize: 15,
            pl: 1,
          }}
        >
          {getText('AUTH')}
        </Typography>
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Tooltip placement="right" title={getLoginTooltip(loginTooltips)}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="login"
              label={getText('LOGIN')}
              name="login"
              autoComplete="login"
              inputProps={{ maxLength: '20' }}
              value={loginInput}
              size="small"
              onChange={onChangeLoginInput}
            />
          </Tooltip>
          <Tooltip placement="right" title={getLoginTooltip(passwordTooltips)}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={getText('PASSWORD')}
              type="password"
              id="password"
              autoComplete="current-password"
              inputProps={{ maxLength: '20' }}
              value={passwordInput}
              size="small"
              onChange={onChangePasswordInput}
              helperText={
                isError || errorText ? (
                  <Typography color={theme.palette.error.main} align={'center'}>
                    {errorText}
                  </Typography>
                ) : null
              }
            />
          </Tooltip>

          <Box display={'flex'} justifyContent={'center'} sx={{ mt: 2 }}>
            <LoadingButton
              loading={isLoad}
              type="submit"
              variant="contained"
              sx={{ width: '40%' }}
            >
              {getText('LOG_BTN')}
            </LoadingButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
